import React, { useState, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import PropTypes, { object } from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import qs from 'query-string';
import isEqual from 'lodash.isequal';
import { useTranslation } from 'react-i18next';
import Header from "../../../../custom/Header/Header"
//
import filtersMeta from './filtersMeta.js';
import { useAppConfig } from '../../state';
import { useDebounce, useSearchParams } from '../../hooks';
import { utils, hotkeys } from '@ohif/core';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver';
import {
  Icon,
  StudyListExpandedRow,
  EmptyStudies,
  StudyListTable,
  StudyListPagination,
  StudyListFilter,
  TooltipClipboard,
  // Header,
  useModal,
  AboutModal,
  UserPreferences,
  LoadingIndicatorProgress,
  useSessionStorage,
  InvestigationalUseDialog,
  Button,
  ButtonEnums,
  ConfirmationPopup,
  Loader,
  SuccessModal,
  Pagination,
  Tooltip,
} from '@ohif/ui';
import i18n from '@ohif/i18n';
import { getCloudPACSToken, removeUserCredentials } from '../../../../custom/Utils/authUtils';
import { frontendBaseURl } from '../../../../custom/constant';
import { filterStudies, getReferPhysicianInfo } from '../../../../custom/Apis/apis';
import { useDispatch, useSelector } from 'react-redux';
import studyData from '@custom/rtk-apis/DCM4CHEE/studyData';
import { _dcm4cheeUrls, _deleteDicomUrl, _studyDataUrls } from '@custom/Utils/constants/apiUrls';
import MergeStudies from './MergeStudies';
import { requiredFieldsForExcel } from './enums';
import addStudyResearch from '@custom/rtk-apis/studyResearch/addStudyResearch';
import { closeModal } from '@custom/store/slices/StudyResearch/addStudyResearchSlice'
import { AppDispatch, RootState } from '@custom/store/store';
import { customStyle } from '@custom/Utils/utils';
import createIsoImage from '@custom/rtk-apis/media-service/mediaService';
import { useTheme } from "../../../../ui/src/contextProviders"
import ShareStudies from './ShareStudies';
import generateLink from '@custom/rtk-apis/media-service/shareStudyLink';
import getCancelledStudiesApi from '@custom/rtk-apis/teleradiologist/getCancelledStudy';
import appointmentDetailsbyUID from '@custom/rtk-apis/appointment/getAppointmentbyUID';
import { _roleType } from '@custom/Utils/constants/appConstants';
import hospitalDetailApi from '@custom/rtk-apis/hospital/hospitalDetails';
import JSZip from 'jszip';
import moveToExtraStorage from '@custom/rtk-apis/extraStorage/moveToExtraStorage';

import subscribedStudies from '@custom/rtk-apis/subscription/subscribedStudies';
import getAIStudiesApi from '@custom/rtk-apis/appointment/getAIStudies';
import { revertStatus } from '@custom/store/slices/Appointment/getAIStudies';

const { sortBySeriesDate } = utils;
// @ts-ignore/
const { availableLanguages, defaultLanguage, currentLanguage } = i18n;

const seriesInStudiesMap = new Map();

const DownloadProgressBar = ({ progress }: { progress: number | null }) => {
  if (progress === null) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '6px',
      backgroundColor: '#ccc',
      zIndex: 9999,
    }}>
      <div style={{
        width: `${progress}%`,
        height: '100%',
        backgroundColor: '#4caf50',
        transition: 'width 0.3s ease-in-out'
      }} />
    </div>
  );
};

/**
 * TODO:
 * - debounce `setFilterValues` (150ms?)
 */

function WorkList({
  data: studies,
  dataTotal: studiesTotal,
  isLoadingData,
  dataSource,
  hotkeysManager,
  dataPath,
  onRefresh,
  servicesManager,
}: withAppTypes) {
  const { hotkeyDefinitions, hotkeyDefaults } = hotkeysManager;
  const { show, hide } = useModal();
  const { t } = useTranslation();
  // ~ Modes
  const [appConfig] = useAppConfig();
  // ~ Filters
  const dispatch = useDispatch<AppDispatch>()
  const state = useSelector((state:RootState) => state)
  const searchParams = useSearchParams();
  const navigate = useNavigate();
  const STUDIES_LIMIT = 101;
  const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
  const hospitalCode = sessionStorage.getItem('hospitalCode') || localStorage.getItem('hospitalCode');
  const queryFilterValues = _getQueryFilterValues(searchParams);
  const isDarkMode = useTheme()
  const [sessionQueryFilterValues, updateSessionQueryFilterValues] = useSessionStorage({
    key: 'queryFilterValues',
    defaultValue: queryFilterValues,
    // ToDo: useSessionStorage currently uses an unload listener to clear the filters from session storage
    // so on systems that do not support unload events a user will NOT be able to alter any existing filter
    // in the URL, load the page and have it apply.
    clearOnUnload: true,
  });
  const [filterValues, _setFilterValues] = useState({
    ...defaultFilterValues,
    ...sessionQueryFilterValues,
  });

  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
  // Select state from Redux store
  const { result: aiStudyData } = useSelector((state: RootState) => state.getAIStudiesResponse);

  const debouncedFilterValues = useDebounce(filterValues, 200);
  const { resultsPerPage, pageNumber, sortBy, sortDirection } = filterValues;

  /*
   * The default sort value keep the filters synchronized with runtime conditional sorting
   * Only applied if no other sorting is specified and there are less than 101 studies
   */

  const canSort = studiesTotal < STUDIES_LIMIT;
  const shouldUseDefaultSort = sortBy === '' || !sortBy;
  const sortModifier = sortDirection === 'descending' ? 1 : -1;
  const defaultSortValues =
    shouldUseDefaultSort && canSort ? { sortBy: 'studyDate', sortDirection: 'ascending' } : {};
  const sortedStudies = studies;

  if (canSort) {
    studies.sort((s1, s2) => {
      if (shouldUseDefaultSort) {
        const ascendingSortModifier = -1;
        return _sortStringDates(s1, s2, ascendingSortModifier);
      }

      const s1Prop = s1[sortBy];
      const s2Prop = s2[sortBy];

      if (typeof s1Prop === 'string' && typeof s2Prop === 'string') {
        return s1Prop.localeCompare(s2Prop) * sortModifier;
      } else if (typeof s1Prop === 'number' && typeof s2Prop === 'number') {
        return (s1Prop > s2Prop ? 1 : -1) * sortModifier;
      } else if (!s1Prop && s2Prop) {
        return -1 * sortModifier;
      } else if (!s2Prop && s1Prop) {
        return 1 * sortModifier;
      } else if (sortBy === 'studyDate') {
        return _sortStringDates(s1, s2, sortModifier);
      }

      return 0;
    });
  }

  const { uiNotificationService } = servicesManager.services

  // ~ Rows & Studies
  const [expandedRows, setExpandedRows] = useState([]);
  const [studiesWithSeriesData, setStudiesWithSeriesData] = useState([]);
  const [studiesToSend, setStudiesToSend] = useState([]);
  const [isPopupOpen, setisPopupOpen] = useState(false)
  const [isDeleteModalOpen, setisDeleteModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [mergeModal, setMergeModal] = useState(false)
  const [showAddResearchSuccess, setShowAddResearchSuccess] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [shareModal, setShareModal] = useState(false)
  const [PhysicianDetails, setPhysicianDetails] = useState({})
  const role = sessionStorage.getItem('role') || localStorage.getItem('role');
  const [downloadProgress, setDownloadProgress] = useState<number | null>(null);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [subscribedStudiesData, setSubscribedStudiesData] = useState([])
  const [isCaseToCase, setIsCaseToCase] = useState(false)

  const [extractedValues, setExtractedValues] = useState([]);
  useEffect(() => {
    if (sortedStudies.length > 0) {
      const qidoStudies = sortedStudies.map(study => study.qidoStudy);
      setExtractedValues(qidoStudies);
    }
  }, [sortedStudies]);

  const numOfStudies = studiesTotal;
  const querying = useMemo(() => {
    return isLoadingData || expandedRows.length > 0;
  }, [isLoadingData, expandedRows]);

  const setFilterValues = val => {
    if (filterValues.pageNumber === val.pageNumber) {
      val.pageNumber = 1;
    }
    _setFilterValues(val);
    updateSessionQueryFilterValues(val);
    setExpandedRows([]);
  };

  const onPageNumberChange = newPageNumber => {
    const oldPageNumber = filterValues.pageNumber;
    const rollingPageNumberMod = Math.floor(101 / filterValues.resultsPerPage);
    const rollingPageNumber = oldPageNumber % rollingPageNumberMod;
    const isNextPage = newPageNumber > oldPageNumber;
    const hasNextPage = Math.max(rollingPageNumber, 1) * resultsPerPage < numOfStudies;

    if (isNextPage && !hasNextPage) {
      return;
    }

    setFilterValues({ ...filterValues, pageNumber: newPageNumber });
  };

  const onResultsPerPageChange = newResultsPerPage => {
    setFilterValues({
      ...filterValues,
      pageNumber: 1,
      resultsPerPage: Number(newResultsPerPage),
    });
  };

  // Set body style
  useEffect(() => {
    return () => {
      document.body.classList.remove('bg-black');
    };

  }, []);

  // Sync URL query parameters with filters
  useEffect(() => {
    if (!debouncedFilterValues) {
      return;
    }

    const queryString = {};
    Object.keys(defaultFilterValues).forEach(key => {
      const defaultValue = defaultFilterValues[key];
      const currValue = debouncedFilterValues[key];

      // TODO: nesting/recursion?
      if (key === 'studyDate') {
        if (currValue?.startDate && defaultValue.startDate !== currValue.startDate) {
          queryString.startDate = currValue.startDate;
        }
        if (currValue?.endDate && defaultValue.endDate !== currValue.endDate) {
          queryString.endDate = currValue.endDate;
        }
      } else if (key === 'modalities' && currValue?.length) {
        queryString.modalities = currValue.join(',');
      } else if (currValue !== defaultValue) {
        queryString[key] = currValue;
      }
    });

    const search = qs.stringify(queryString, {
      skipNull: true,
      skipEmptyString: true,
    });

    navigate({
      pathname: '/',
      search: search ? `?${search}` : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilterValues]);

  // Query for series information
  useEffect(() => {
    const fetchSeries = async studyInstanceUid => {
      try {
        const series = await dataSource.query.series.search(studyInstanceUid);
        seriesInStudiesMap.set(studyInstanceUid, sortBySeriesDate(series));
        setStudiesWithSeriesData([...studiesWithSeriesData, studyInstanceUid]);
      } catch (ex) {
        // TODO: UI Notification Service
        console.warn(ex);
      }
    };

    // TODO: WHY WOULD YOU USE AN INDEX OF 1?!
    // Note: expanded rows index begins at 1
    for (let z = 0; z < expandedRows.length; z++) {
      const expandedRowIndex = expandedRows[z] - 1;
      const studyInstanceUid = sortedStudies[expandedRowIndex].studyInstanceUid;

      if (studiesWithSeriesData.includes(studyInstanceUid)) {
        continue;
      }

      fetchSeries(studyInstanceUid);
    }

    const hospitalCode = sessionStorage.getItem('hospitalCode') || "";

    dispatch(subscribedStudies({hospitalCode})).unwrap()
      .then((res) => {
        console.log('response for subscribed studies',res);
        if(res.status === 'SUCCESS' && res.msg == 'FOUND'){
          setSubscribedStudiesData(res.data.studies)
          if(res?.data?.plan_code == 'CASE_TO_CASE_PLAN'){
            setIsCaseToCase(true)
          }
        }

      })
      .catch((err) => {
        console.error('Error during API call:', err);
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedRows, studies]);

  const isFiltering = (filterValues, defaultFilterValues) => {
    return !isEqual(filterValues, defaultFilterValues);
  };

  const rollingPageNumberMod = Math.floor(101 / resultsPerPage);
  const rollingPageNumber = (pageNumber - 1) % rollingPageNumberMod;
  const offset = resultsPerPage * rollingPageNumber;
  const offsetAndTake = offset + resultsPerPage;
  useEffect(() => {
    if (sortedStudies.length > 0) {
      const qidoStudies = sortedStudies.map(study => study.qidoStudy);
      setExtractedValues(qidoStudies);
    }
  }, [sortedStudies]);

  const tableDataSource = sortedStudies.map((study, key) => {
    const rowKey = key + 1;
    const isExpanded = expandedRows.some(k => k === rowKey);
    const {
      studyInstanceUid,
      accession,
      modalities,
      instances,
      description,
      mrn,
      patientName,
      date,
      time,
    } = study;
    const studyDate =
      date &&
      moment(date, ['YYYYMMDD', 'YYYY.MM.DD'], true).isValid() &&
      moment(date, ['YYYYMMDD', 'YYYY.MM.DD']).format(t('Common:localDateFormat', 'MMM-DD-YYYY'));
    const studyTime =
      time &&
      moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).isValid() &&
      moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).format(
        t('Common:localTimeFormat', 'hh:mm A')
      );

    return {
      dataCY: `studyRow-${studyInstanceUid}`,
      clickableCY: studyInstanceUid,
      row: [
        {
          key: 'patientName',
          content: patientName ? (
            <TooltipClipboard>{patientName}</TooltipClipboard>
          ) : (
            <span className="text-gray-700">(Empty)</span>
          ),
          gridCol: 4,
        },
        // {
        //   key: 'mrn',
        //   content: <TooltipClipboard>{mrn}</TooltipClipboard>,
        //   gridCol: 3,
        // },
        {
          key: 'studyDate',
          content: (
            <>
              {studyDate && <span className="mr-4">{studyDate}</span>}
              {studyTime && <span>{studyTime}</span>}
            </>
          ),
          title: `${studyDate || ''} ${studyTime || ''}`,
          gridCol: 5,
        },
        {
          key: 'description',
          content: <TooltipClipboard>{description}</TooltipClipboard>,
          gridCol: 4,
        },
        {
          key: 'modality',
          content: modalities,
          title: modalities,
          gridCol: 3,
        },
        {
          key: 'CaseStudy',
          content:
            '--'
         ,
          gridCol: 3,
        },
        {
          key: 'isExtraStorage',
          content:
            '--'
         ,
          gridCol: 2,
        },
        // {
        //   key: 'accession',
        //   content: <TooltipClipboard>{accession}</TooltipClipboard>,
        //   gridCol: 3,
        // },
        // {
        //   key: 'instances',
        //   content: (
        //     <>
        //       <Icon
        //         name="group-layers"
        //         className={classnames('mr-2 inline-flex w-4', {
        //           'text-primary-active': isExpanded,
        //           'text-secondary-light': !isExpanded,
        //         })}
        //       />
        //       {instances}
        //     </>
        //   ),
        //   title: (instances || 0).toString(),
        //   gridCol: 2,
        // },
      ],
      // Todo: This is actually running for all rows, even if they are
      // not clicked on.
      expandedContent: (
        <StudyListExpandedRow
          seriesTableColumns={{
            description: t('StudyList:Description'),
            seriesNumber: t('StudyList:Series'),
            modality: t('StudyList:Modality'),
            instances: t('StudyList:Instances'),
          }}
          seriesTableDataSource={
            seriesInStudiesMap.has(studyInstanceUid)
              ? seriesInStudiesMap.get(studyInstanceUid).map(s => {
                return {
                  description: s.description || '(empty)',
                  seriesNumber: s.seriesNumber ?? '',
                  modality: s.modality || '',
                  instances: s.numSeriesInstances || '',
                };
              })
              : []
          }
        >
          <div className="flex flex-row gap-2">
            {(appConfig.groupEnabledModesFirst
              ? appConfig.loadedModes.sort((a, b) => {
                const isValidA = a.isValidMode({
                  modalities: modalities.replaceAll('/', '\\'),
                  study,
                }).valid;
                const isValidB = b.isValidMode({
                  modalities: modalities.replaceAll('/', '\\'),
                  study,
                }).valid;

                return isValidB - isValidA;
              })
              : appConfig.loadedModes
            ).map((mode, i) => {
              const modalitiesToCheck = modalities.replaceAll('/', '\\');

              const { valid: isValidMode, description: invalidModeDescription } = mode.isValidMode({
                modalities: modalitiesToCheck,
                study,
              });
              const query = new URLSearchParams();
              if (filterValues.configUrl) {
                query.append('configUrl', filterValues.configUrl);
              }
              query.append('StudyInstanceUIDs', studyInstanceUid);
              query.append('modalitytype', modalities);
              return (
                mode.displayName && (
                  <Link
                    className={isValidMode ? '' : 'cursor-not-allowed'}
                    key={i}
                    to={`${dataPath ? '../../' : ''}${mode.routeName}${dataPath || ''
                      }?${query.toString()}`}
                    onClick={event => {
                      // In case any event bubbles up for an invalid mode, prevent the navigation.
                      // For example, the event bubbles up when the icon embedded in the disabled button is clicked.
                      if (!isValidMode) {
                        event.preventDefault();
                      }
                    }}
                  // to={`${mode.routeName}/dicomweb?StudyInstanceUIDs=${studyInstanceUid}`}
                  >
                    {/* TODO revisit the completely rounded style of buttons used for launching a mode from the worklist later - for now use LegacyButton*/}
                    <Button
                      type={ButtonEnums.type.primary}
                      size={ButtonEnums.size.medium}
                      disabled={!isValidMode}
                      startIconTooltip={
                        !isValidMode ? (
                          <div className="font-inter flex w-[206px] whitespace-normal text-left text-xs font-normal text-white	">
                            {invalidModeDescription}
                          </div>
                        ) : null
                      }
                      startIcon={
                        <Icon
                          className="!h-[20px] !w-[20px] text-black"
                          name={isValidMode ? 'launch-arrow' : 'launch-info'}
                        />
                      } // launch-arrow | launch-info
                      onClick={() => { }}
                      dataCY={`mode-${mode.routeName}-${studyInstanceUid}`}
                      className={isValidMode ? 'text-[13px]' : 'bg-[#222d44] text-[13px]'}
                    >
                      {mode.displayName}
                    </Button>
                  </Link>
                )
              );
            })}
          </div>
        </StudyListExpandedRow>
      ),
      onClickRow: () => {
        let isStudyValid = false
        console.log("subscribedStudiesData",subscribedStudiesData)

        if(subscribedStudiesData?.length > 0 && isCaseToCase) {
            isStudyValid = subscribedStudiesData?.some(
                study => study?.studyInstanceUID === studyInstanceUid && study?.creditsPaid === true
            );
        }

        else if(subscribedStudiesData?.length > 0) {
          isStudyValid = subscribedStudiesData?.some(
            study => study?.studyInstanceUID === studyInstanceUid
          );
        }
        else{
          isStudyValid = true
        }

        if(isStudyValid) {
          setExpandedRows(s => (isExpanded ? s.filter(n => rowKey !== n) : [...s, rowKey]))
        }
        else if(isStudyValid === false && isCaseToCase){
          uiNotificationService.show({
            position: "topRight",
            type: "error",
            message: "Your available credits have been used or Please contact the administrator for assistance.",
            title: "Access Restricted"
          })
          return
        }
        else if(isStudyValid === false){
          uiNotificationService.show({
            position: "topRight",
            type: "error",
            message: "Your current subscription does not include access to this study. Please upgrade to continue.",
            title: "Access Restricted"
          })
          return
        }
      },
      isExpanded,
      rawStudyDetails: study
    };
  }).map((y)=>{
    if (state.appointmentDetailbyUIDresponse?.result?.length > 0) {
      const filteredItem = state.appointmentDetailbyUIDresponse.result.find(x =>
        x?.patientInfo?.UHID === y.rawStudyDetails.mrn &&
        x?.patientInfo?.patientId === y.rawStudyDetails.accession
      );

      if (filteredItem) {
        const caseStudyIndex = y.row.findIndex(t => t.key === "CaseStudy");
        if (caseStudyIndex !== -1) {
          y.row[caseStudyIndex].content =
          <Tooltip
          content={`${filteredItem?.priority} ${
                (role === 'Superadmin' || role === _roleType.radiologistAdmin) ? `- ${filteredItem?.hospital?.hospitalName}` : ''
          }`}
          >
            <span
            className={`truncate ${
              filteredItem.priority === 'Critical'
                ? 'text-red-600'
                : filteredItem.priority === 'Routine'
                ? 'text-green-600'
                : 'text-blue-600'
            }`}
            >
            {filteredItem.priority}{' '}
            {role === 'Superadmin' || role === _roleType.radiologistAdmin ? `- ${filteredItem?.hospital?.hospitalName}` : ''}
            </span>
          </Tooltip>

        }
      }
    }

  const matchedStudy = subscribedStudiesData?.find(
  study => study.studyInstanceUID === y.clickableCY
  );
  y.row[5].content = matchedStudy?.isExtraStorage ?
         <Tooltip
          content={`Extra Storage`}
          >
            <Icon
              className="h-[38px] w-[38px]"
              name={'extraStorageIcon'}
            />
          </Tooltip> : '--'

    return y
  });

  const hasStudies = numOfStudies > 0;
  const versionNumber = process.env.VERSION_NUMBER;
  const commitHash = process.env.COMMIT_HASH;

  const menuOptions = [
    {
      title: t('Header:Patients'),
      icon: "profile",
      onClick: () => {
        navigate("/patientlist")
      }
    },
    {
      title: t('Header:About'),
      icon: 'info',
      onClick: () =>
        show({
          content: AboutModal,
          title: t('AboutModal:About OHIF Viewer'),
          contentProps: { versionNumber, commitHash },
          containerDimensions: 'max-w-4xl max-h-4xl',
        }),
    },
    {
      title: t('Header:Preferences'),
      icon: 'settings',
      onClick: () =>
        show({
          title: t('UserPreferencesModal:User preferences'),
          content: UserPreferences,
          contentProps: {
            hotkeyDefaults: hotkeysManager.getValidHotkeyDefinitions(hotkeyDefaults),
            hotkeyDefinitions,
            onCancel: hide,
            currentLanguage: currentLanguage(),
            availableLanguages,
            defaultLanguage,
            onSubmit: state => {
              if (state.language.value !== currentLanguage().value) {
                i18n.changeLanguage(state.language.value);
              }
              hotkeysManager.setHotkeys(state.hotkeyDefinitions);
              hide();
            },
            onReset: () => hotkeysManager.restoreDefaultBindings(),
            hotkeysModule: hotkeys,
          },
        }),
    },
    {
      icon: 'power-off',
      title: t('Header:Logout'),
      onClick: () => {
        removeUserCredentials();
        window.location.href = frontendBaseURl + 'login';
      },
    },
  ];

  if (appConfig.oidc) {
    menuOptions.push({
      icon: 'power-off',
      title: t('Header:Logout'),
      onClick: () => {
        navigate(`/logout?redirect_uri=${encodeURIComponent(window.location.href)}`);
      },
    });
  }

  const { customizationService } = servicesManager.services;
  const { component: dicomUploadComponent } =
    customizationService.get('dicomUploadComponent') ?? {};
  const uploadProps =
    dicomUploadComponent && dataSource.getConfig()?.dicomUploadEnabled
      ? {
        title: 'Upload files',
        closeButton: true,
        shouldCloseOnEsc: false,
        shouldCloseOnOverlayClick: false,
        content: dicomUploadComponent.bind(null, {
          dataSource,
          onComplete: () => {
            hide();
            onRefresh();
          },
          onStarted: () => {
            show({
              ...uploadProps,
              // when upload starts, hide the default close button as closing the dialogue must be handled by the upload dialogue itself
              closeButton: false,
            });
          },
        }),
      }
      : undefined;

  const { component: dataSourceConfigurationComponent } =
    customizationService.get('ohif.dataSourceConfigurationComponent') ?? {};


  const transformDataForExcel = (inputArray, requiredFields) => {
    return inputArray.map(item => {
      const result = {};
      Object.keys(requiredFields).forEach(tag => {
        const field = requiredFields[tag];
        if (item[tag]) {
          // If Value exists, handle nested objects
          let value = item[tag].Value ? item[tag].Value[0] : '';

          // If the value is an object (like { "Alphabetic": "Doe^Harry" }), extract the first key's value
          if (typeof value === 'object' && value !== null) {
            value = Object.values(value)[0];
          }

          result[field] = value;
        } else {
          result[field] = '';
        }
      });
      return result;
    });
  };

  function capitalizeFirstLetter(string) {
    if (!string) return "";
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  const duplicateStudyHandler = () => {
    if (studiesToSend.length < 1) {
      uiNotificationService.show({
        position: "topRight",
        type: "error",
        message: "Please select atleast one study",
        title: "Warning"
      })
      return
    }
    setisPopupOpen(true)
  }

  const handleDownloadExcel = async () => {
    const { result } = state?.studyDataResponse;
    const modifiedData = transformDataForExcel(result, requiredFieldsForExcel);

    const data = modifiedData;

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Set column headers and width dynamically
    worksheet.columns = Object.keys(data[0]).map((d) => {
      const longestValueLength = Math.max(...data.map((item) => (item[d] ? item[d].toString().length : 0)), d.length);
      return {
        header: capitalizeFirstLetter(d),
        key: d,
        width: Math.max(20, longestValueLength + 5), // Adjust width dynamically
      };
    });

    // Apply bold font to the header row
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).alignment = { horizontal: 'center' }; // Center align header text

    // Add rows and apply some basic formatting
    data.forEach((item) => {
      const row = worksheet.addRow(item);
      row.alignment = { vertical: 'middle', wrapText: true }; // Vertically align and wrap text
    });

    // Add borders to all cells for better readability
    worksheet.eachRow((row, rowNumber) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    });

    // Generate buffer and save the file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'studies.xlsx');
  };
  const removeKey = (data) => {
    const updatedData = data.map((item) => {
      const { qidoStudy, ...rest } = item;
      return rest;
    });
    return updatedData
  }

  function generateDuplicateUID(oid) {
    let randomNumber = Math.floor(Math.random() * (999 - 100 + 1)) + 100;
    const modifiedString = `${oid}_duplicate${randomNumber}`
    return `${modifiedString}`;
  }

  const handleConfirm = () => {
    setLoading(true)
    let count = 0;
    if (studiesToSend.length > 0) {
      studiesToSend.map(async (study) => {
        const newStudyInstanceUID = generateDuplicateUID(study.studyInstanceUid)

        try {
          let url = `${_studyDataUrls.duplicate(newStudyInstanceUID)}`
          const pacs_token = getCloudPACSToken();
          let response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${pacs_token}`,
            },
            body: JSON.stringify({
              'StudyInstanceUID': study.studyInstanceUid
            })
          })
          if (response.status >= 200 && response.status <= 300) {
            const responseBody = await response.json()
            ++count

            if (count == studiesToSend.length) {
              setTitle('Studies duplicated successfully.')
              setIsSuccessModalOpen(true)
              clearFilters()
              setLoading(false)
              const url = window.location.origin
              setTimeout(() => {
                window.location.href = url
              }, 2000);
            }
          }
        } catch (error) {
          return error;
        } finally {
          setLoading(false)
        }
      })

    } else {
      uiNotificationService.show({
        title: "warning",
        message: 'Please select atleast one study',
        type: "error",
        duration: 5000,
        position: "topRight"
      })
    }
    setisPopupOpen(false)
  }

  const handleCancel = () => {
    setisPopupOpen(false)
  }

  const clearFilters = () => {
    setFilterValues(defaultFilterValues)
  }

  const handleAddToResearch = async () => {

    if (studiesToSend.length === 0) {
      uiNotificationService.show({
        title: "Warning",
        message: 'Please select atleast one study',
        type: "error",
        position: "topRight"
      })
      return;
    }

    const payload = studiesToSend?.map((item) => {
      return {
        patientId: item.accession,
        uhid: item.mrn,
        patientName: item.patientName,
        dob: item.patientDob,
        gender: item.gender === "M" ? "Male" : item.gender === "F" ? "Female" : "Others",
        modality: item.modalities,
        studyRegion: item.description || "",
        referringPhysician: item.referringPhysician || "",
        studyDate: item.date || "",
        studyInstanceUID: item.studyInstanceUid,
        // studyItem: item,
      }
    });

    dispatch(addStudyResearch({ studyArray: payload }));
  }
  const handleMoveToExtraStorage = async () => {

    // @ts-ignore
    const extraStoragePurchased = state?.hospitalDetailsResponse?.result?.subscriptionDetail?.extraStoragePurchasedInKB || 0;

    if(extraStoragePurchased == 0){
      uiNotificationService.show({
        title: "Warning",
        message: 'Please purchase extra storage plan to move studies to switch storage',
        type: "warning",
        position: "topRight"
      })
      return;
    }
    if (studiesToSend.length === 0) {
      uiNotificationService.show({
        title: "Warning",
        message: 'Please select atleast one study',
        type: "error",
        position: "topRight"
      })
      return;
    }


    const studyUids = studiesToSend.map(item => item.studyInstanceUid);

    const payload =  {
        studies: studyUids,
        hospitalCode: sessionStorage.getItem('hospitalCode') || localStorage.getItem('hospitalCode'),
      }
    setLoading(true);


    dispatch(moveToExtraStorage(payload)).then((res)=>{
      // @ts-ignore
      if(res.payload.status == 'SUCCESS'){
        setTimeout(() => {
          setLoading(false);
          uiNotificationService.show({
            title: "Success",
            message: 'Storage switched successfully. Please reload the page after a few moments to see the updated changes.',
            type: "success",
            position: "topRight"
          })

          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }, 4000);
      }
    }).catch((err) => {
       setTimeout(() => {
          setLoading(false);
          uiNotificationService.show({
            title: "Failure",
            message: 'Something went wrong. Try again later',
            type: "error",
            position: "topRight"
          })
        }, 2000);
    }).finally(() => {
      setTimeout(() => {
        setLoading(false);
      }, 4000);
    });
  }

  const createUniqueFileName = (name) => {
    const timestamp = Date.now(); // Get the current timestamp
    const uniqueId = Math.random().toString(36).substr(2, 9); // Generate a short random string
    return `${name}_${timestamp}_${uniqueId}.zip`;
  };

  const handleDownloadDicomClick = async () => {
    if (studiesToSend.length === 0) {
      uiNotificationService.show({
        title: "Warning",
        message: "Please select at least one study",
        type: "error",
        position: "topRight",
      });
      return;
    }

    setDownloadProgress(0);

    const downloadRequests = studiesToSend.map((study) => ({
      studyInstanceUID: study?.studyInstanceUid,
      seriesInstanceUID: "",
      sopInstanceUID: "",
      downloadType: "STUDY",
      downloadFormat: "DCM",
    }));

    const pacsToken = getCloudPACSToken();

    try {
      const fetchPromises = downloadRequests.map((req) =>
        fetch(_dcm4cheeUrls(req), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${pacsToken}`,
          },
        })
      );

      const responses = await Promise.all(fetchPromises);
      const failedResponses = responses.filter((res) => !res.ok);

      if (failedResponses.length > 0) {
        throw new Error(
          `Failed to fetch some files. Statuses: ${failedResponses
            .map((res) => res.status)
            .join(", ")}`
        );
      }

      const blobs = await Promise.all(
        responses.map(async (res, index) => {
          const blob = await res.blob();
          setDownloadProgress(Math.round(((index + 1) / responses.length) * 40)); // 0-40%
          return blob;
        })
      );

      const mergedZip = new JSZip();

      for (let i = 0; i < blobs.length; i++) {
        const zip = await JSZip.loadAsync(blobs[i]);
        const entries = [];

        zip.forEach((relativePath, file) => {
          if (!file.dir) {
            entries.push(
              file.async("blob").then((blob) => {
                mergedZip.file(`${i}_${relativePath}`, blob);
              })
            );
          }
        });

        await Promise.all(entries);
        setDownloadProgress(40 + Math.round(((i + 1) / blobs.length) * 40)); // 40–80%
      }

      const mergedBlob = await mergedZip.generateAsync(
        { type: "blob" },
        (metadata) => {
          const progress = 80 + Math.round((metadata.percent / 100) * 20); // 80–100%
          setDownloadProgress(progress);
        }
      );

      const downloadUrl = URL.createObjectURL(mergedBlob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `${createUniqueFileName("patName")}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);

      uiNotificationService.show({
        title: "Success",
        message: "All files downloaded successfully",
        type: "success",
        duration: 3000,
        position: "topRight",
      });
    } catch (error: any) {
      console.error(error);
      uiNotificationService.show({
        title: "Error",
        message: `Failed to download files: ${error.message}`,
        type: "error",
        duration: 3000,
        position: "topRight",
      });
    } finally {
      setDownloadProgress(null);
    }
  };

  const handleDeleteDicomClick = async () => {
    if (studiesToSend.length < 1) {
      uiNotificationService.show({
        title: "Warning",
        type: "warning",
        message: "Please select atleast one study",
        position: 'topRight',
        duration: 2000
      })
      return
    }
    setisDeleteModalOpen(true);
  }

  const handleConfirmDelete = async () => {
    const pacsToken = getCloudPACSToken();

    try {
      setLoading(true)
      const fetchPromises = studiesToSend.map((study) =>
        fetch(_deleteDicomUrl(study.studyInstanceUid), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${pacsToken}`,
          },
        })
      );

      const responses = await Promise.all(fetchPromises);
      const failedResponses = responses.filter((res) => !res.ok);

      if (failedResponses.length > 0) {
        throw new Error(
          `Failed to fetch some files. Statuses: ${failedResponses
            .map((res) => res.status)
            .join(", ")}`
        );
      }

      uiNotificationService.show({
        title: "Success",
        message: "All files deleted successfully",
        type: "success",
        duration: 3000,
        position: "topRight",
      })
    } catch (error) {
      console.log(error)
      uiNotificationService.show({
        title: "Error",
        message: `Failed to delete files: ${error.message}`,
        type: "error",
        duration: 3000,
        position: "topRight",
      })
    }
    finally {
      setLoading(false);
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  }

  const handleCancelDelete = () => {
    setisDeleteModalOpen(false);
  }

  const handleCreateISO = async () => {

    if (studiesToSend.length < 1) {
      uiNotificationService.show({
        title: "Warning",
        type: "warning",
        message: "Please select atleast one study",
        position: 'topRight',
        duration: 2000
      })
      return
    }

    const studyIds = studiesToSend.map((study) => {
      return study.studyInstanceUid
    })
    const payload = {
      studyIds: studyIds
    }

    dispatch(createIsoImage(payload))
  }


  const getPhysicianInfo = async () => {
    try {
      const payload = {
        uhid: studiesToSend[0]?.mrn
      }
      const response = await getReferPhysicianInfo(payload?.uhid)
      if (response?.status === "SUCCESS") {
        setPhysicianDetails(response?.data[0])
      }
    } catch (error) {
      console.log(error, " getPhysicianInfo error")
    }
  }

  const handleSelectAllClick = () => {
    setIsSelectAll(!isSelectAll);
    setStudiesToSend(()=>{
      if(isSelectAll){
        return []
      }
      else{
        return tableDataSource?.map((study) => study?.rawStudyDetails);
      }
    })
  }

  // Share Study link
  const generateStudyLinkHandler = () => {
    if (studiesToSend.length !== 1) {
      uiNotificationService.show({
        title: "Warning",
        type: "warning",
        message: studiesToSend.length > 1 ? "Please select only one study" : studiesToSend.length <= 0 ? "Please select atleast one study" : '',
        position: 'topRight',
        duration: 2000
      })
      return
    }
    dispatch(generateLink(studiesToSend[0].studyInstanceUid))
    setShareModal(true)
    getPhysicianInfo();
  }
  useEffect(() => {
    dispatch(studyData())
    dispatch(hospitalDetailApi({ hospitalId: hospitalId }));
  }, [])

  useEffect(() => {
    if (state?.addStudyReserach?.status === "SUCCESS") {
      setTitle("Study Research added successfully.");
      setShowAddResearchSuccess(true);
    }
    else if (state?.addStudyReserach?.status === "ERROR" || state?.addStudyReserach?.status === "failure") {
      uiNotificationService.show({
        title: "Error",
        message: state?.addStudyReserach?.msg,
        type: "error",
        position: "topRight"
      })
    }

    dispatch(closeModal());

  }, [state?.addStudyReserach?.status])

  const getCancelledStudy = async () => {
    await dispatch(getCancelledStudiesApi()).unwrap()
      .then((res) => {

      }).catch((err) => {
        console.log("errorr", err);
      });
  }

  useEffect(() => {
    getCancelledStudy();
  }, [])

  useEffect(()=>{
    let payload = sortedStudies.map((x)=>{
      return {patientId:x.accession, UHID: x.mrn }
    })
    console.log("sortedStudies",sortedStudies)
    if(payload.length > 0){
      dispatch(appointmentDetailsbyUID(payload))
    }
  },[studies,filterValues])


  // Fetch AI Studies data
  const fetchAIStudies = async () => {
    if (hospitalId && hospitalCode) {
      setHasAttemptedFetch(true);
      try {
        const aiStudyResponse = await dispatch(getAIStudiesApi({ hospitalId, hospitalCode })).unwrap();
        console.log("aiStudyResponse",aiStudyResponse)
      } catch (err) {
        console.error('Failed to fetch AI Studies:', err);
      }
    }
  };

  // Auto-fetch on component mount or when dependencies change
  useEffect(() => {
    fetchAIStudies();
    return ()=>{
      setHasAttemptedFetch(false);
      dispatch(revertStatus());
    }
  }, [hospitalId, hospitalCode, dispatch, studies, filterValues]);

  return (
    <>
      <ShareStudies studiesToSend={studiesToSend} setShareModal={setShareModal} shareModal={shareModal} PhysicianDetails={PhysicianDetails} />
      <div className='page page__fullBody pageStudieslayout' style={
        isDarkMode?.isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
      } >
        <Header isBack={false} title='Studies'></Header>
        <div className='page__body'>
          <div className='formCol'>
            <StudyListFilter
              numOfStudies={pageNumber * resultsPerPage > 100 ? 101 : numOfStudies}
              filtersMeta={filtersMeta}
              filterValues={{ ...filterValues, ...defaultSortValues }}
              onChange={setFilterValues}
              clearFilters={clearFilters}
              isFiltering={isFiltering(filterValues, defaultFilterValues)}
              onUploadClick={uploadProps ? () => show(uploadProps) : undefined}
              getDataSourceConfigurationComponent={
                dataSourceConfigurationComponent ? () => dataSourceConfigurationComponent() : undefined
              }
              count={tableDataSource.length}
              studiesToSend={studiesToSend}
              handleDownloadExcel={handleDownloadExcel}
              duplicateStudyHandler={duplicateStudyHandler}
              setMergeModal={setMergeModal}
              handleAddToResearch={handleAddToResearch}
              handleDownloadDicomClick={handleDownloadDicomClick}
              handleCreateISO={handleCreateISO}
              generateStudyLinkHandler={generateStudyLinkHandler}
              isSelectAll={isSelectAll}
              handleSelectAllClick={handleSelectAllClick}
              handleMoveToExtraStorage={handleMoveToExtraStorage}
              handleDeleteDicomClick={handleDeleteDicomClick}
            />
            {hasStudies ? (
              <div className="flex grow flex-col">
                <StudyListTable
                  tableDataSource={tableDataSource.slice(offset, offsetAndTake)}
                  numOfStudies={numOfStudies}
                  querying={querying}
                  filtersMeta={filtersMeta}
                  setStudiesToSend={setStudiesToSend}
                  studiesToSend={studiesToSend}
                  extractedValues={extractedValues}
                  clearFilters={clearFilters}
                  aiStudyData={aiStudyData}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center pt-48">
                {appConfig.showLoadingIndicator && isLoadingData ? (
                  <Loader isLoaderOpen={appConfig.showLoadingIndicator}></Loader>
                ) : (
                  ""
                )}
              </div>
            )}
            {tableDataSource.length < 1 ? <EmptyStudies /> : ""}
          </div>
        </div>
        <DownloadProgressBar progress={downloadProgress} />
        {tableDataSource?.length > 0 &&
          <Pagination
            onChangePage={onPageNumberChange}
            onChangePerPage={onResultsPerPageChange}
            currentPage={pageNumber}
            perPage={resultsPerPage}
            totalPage={tableDataSource?.length}
          />}
        <ConfirmationPopup
          isOpen={isPopupOpen}
          title="Duplicate Studies"
          message={`Are you sure you want duplicate this study ?`}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
        <ConfirmationPopup
          isOpen={isDeleteModalOpen}
          title="Delete Studies"
          message={`Are you sure you want delete the selected studies ?`}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
        <Loader isLoaderOpen={loading || state?.addStudyReserach?.loading || state?.createIsoImageResponse.loading} />
        <MergeStudies studiesToSend={removeKey(studiesToSend)} mergeModal={mergeModal} setMergeModal={setMergeModal} />
        <SuccessModal
          isSuccessModalOpen={isSuccessModalOpen || showAddResearchSuccess}
          title={title}
          handleCloseModal={() => {
            setIsSuccessModalOpen(false);
            setShowAddResearchSuccess(false);
          }
          }
        />
      </div>
    </>
  );
}

WorkList.propTypes = {
  data: PropTypes.array.isRequired,
  dataSource: PropTypes.shape({
    query: PropTypes.object.isRequired,
    getConfig: PropTypes.func,
  }).isRequired,
  isLoadingData: PropTypes.bool.isRequired,
  servicesManager: PropTypes.object.isRequired,
};

const defaultFilterValues = {
  patientName: '',
  mrn: '',
  studyDate: {
    startDate: null,
    endDate: null,
  },
  description: '',
  modalities: [],
  accession: '',
  sortBy: '',
  sortDirection: 'none',
  pageNumber: 1,
  resultsPerPage: 25,
  datasources: '',
  configUrl: null,
  templateHTML: '',
  diagnosis: ""
};

function _tryParseInt(str, defaultValue) {
  let retValue = defaultValue;
  if (str && str.length > 0) {
    if (!isNaN(str)) {
      retValue = parseInt(str);
    }
  }
  return retValue;
}

function _getQueryFilterValues(params) {
  const newParams = new URLSearchParams();
  for (const [key, value] of params) {
    newParams.set(key.toLowerCase(), value);
  }
  params = newParams;

  const queryFilterValues = {
    patientName: params.get('patientname'),
    mrn: params.get('mrn'),
    studyDate: {
      startDate: params.get('startdate') || null,
      endDate: params.get('enddate') || null,
    },
    description: params.get('description'),
    modalities: params.get('modalities') ? params.get('modalities').split(',') : [],
    accession: params.get('accession'),
    sortBy: params.get('sortby'),
    sortDirection: params.get('sortdirection'),
    pageNumber: _tryParseInt(params.get('pagenumber'), undefined),
    resultsPerPage: _tryParseInt(params.get('resultsperpage'), undefined),
    datasources: params.get('datasources'),
    configUrl: params.get('configurl'),
  };

  // Delete null/undefined keys
  Object.keys(queryFilterValues).forEach(
    key => queryFilterValues[key] == null && delete queryFilterValues[key]
  );

  return queryFilterValues;
}

function _sortStringDates(s1, s2, sortModifier) {
  // TODO: Delimiters are non-standard. Should we support them?
  const s1Date = moment(s1.date, ['YYYYMMDD', 'YYYY.MM.DD'], true);
  const s2Date = moment(s2.date, ['YYYYMMDD', 'YYYY.MM.DD'], true);

  if (s1Date.isValid() && s2Date.isValid()) {
    return (s1Date.toISOString() > s2Date.toISOString() ? 1 : -1) * sortModifier;
  } else if (s1Date.isValid()) {
    return sortModifier;
  } else if (s2Date.isValid()) {
    return -1 * sortModifier;
  }
}

export default WorkList;
